
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React, { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { useForm, FieldValues } from 'react-hook-form';
import { css } from '@emotion/react';
import theme from '@styles/theme';
import { REGEX, ERROR_MESSAGE } from '@common/values';
import { alertMessage } from '@slices/alertSlice';
import { useAppDispatch } from '@store/hooks';
import { LOGIN_USER } from '@api/user';
import { loginUserData } from '@customTypes/user';
import { setUserProperties, setUserToken } from '@common/utils';
import { SERVER_ERROR_CODE } from '@common/values';
import { Button, BackHeader, SPLoading, TextInput } from '@components/commons';
import { startSignIn } from '@lib/eventManager';
import { useGetRefreshCountryTokenMutation } from '@/operations/queries/user/__generated__/useGetRefreshCountryToken.generated';
import { useInitUserSystemLanguageMutation } from '@/operations/queries/user/__generated__/useInitUserSystemLanguage.generated';
import setLanguage from 'next-translate/setLanguage';
import { GenerationStoryListDocument } from '@/operations/queries/home/__generated__/useGenerationStoryList.generated';

const LoginPage: NextPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { url, redirectUrl, clientId, scopes: queryScopes } = router.query;
  const [scopes] = useState<string[]>(
    queryScopes
      ? Array.isArray(queryScopes)
        ? queryScopes
        : [queryScopes]
      : [],
  );

  const {
    watch,
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
  } = useForm({ mode: 'onChange' });

  const [loginUser, { client, loading }] = useMutation(LOGIN_USER, {
    onCompleted: (data: loginUserData) => {
      client.resetStore();

      setUserProperties({
        registeredAt: data.loginUser.me.registeredAt,
        isTester: data.loginUser.me.isTester,
        userId: data.loginUser.me.userId,
        email: data.loginUser.me.email,
        language: data.loginUser.me.systemLanguageCode,
        countryCode: data.loginUser.me.countryCode,
        isAdult:
          data.loginUser.me.certificatedAt &&
          !data.loginUser.me.isExpiredCertification,
        isAnonymous: data.loginUser.me.isAnonymous,
      });
      startSignIn({ method: 'E-MAIL' });
      setUserToken({
        token: data.loginUser.token,
      });
      if (redirectUrl) {
        router.push({
          pathname: `/sso`,
          query: {
            redirectUrl,
            clientId,
            scopes,
          },
        });
        return;
      }
      refreshToken();
    },
    onError: (error) => {
      if (error.message.includes(SERVER_ERROR_CODE.EMAIL_INVALID)) {
        dispatch(
          alertMessage({
            message: t('signup_screen_description_invalid_email'),
            type: 'warning',
          }),
        );
      } else {
        dispatch(
          alertMessage({
            message: t('snslogin_toast_title_not_valid_email_or_password'),
            type: 'warning',
          }),
        );
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  const [refreshToken, { loading: refreshTokenLoading }] =
    useGetRefreshCountryTokenMutation({
      onCompleted: (res) => {
        setUserToken({
          token: res.refreshToken?.token,
        });

        initUserSystemLanguage({
          variables: {
            languageCode: router.locale === 'ko' ? 'ko_KR' : 'en_US',
          },
        });
      },
    });

  const [initUserSystemLanguage, { loading: initUserLoading }] =
    useInitUserSystemLanguageMutation({
      onCompleted: async (res) => {
        await setLanguage(
          res.initUserSystemLanguage.systemLanguage?.code === 'ko_KR'
            ? 'ko'
            : 'en',
        );

        router.push(`${url ?? '/'}`);
      },
      refetchQueries: [
        {
          query: GenerationStoryListDocument,
        },
      ],
    });

  const submitForm = (values: FieldValues) => {
    loginUser({
      variables: { email: values.email, password: values.password },
    });
  };

  return (
    <>
      <BackHeader
        title={t('common_screen_title_login')}
        onBack={() => router.back()}
      />
      <main css={container}>
        <form onSubmit={handleSubmit(submitForm)} className="login-form">
          <TextInput
            id="email"
            type="text"
            label={t('common_screen_label_email')}
            error={errors?.email?.message}
            showDeleteIcon={watch()?.email}
            register={register}
            onDelete={() => setValue('email', '')}
            placeholder={t('common_screen_label_email')}
            rules={{
              pattern: {
                value: REGEX.E_MAIL,
                message: t(ERROR_MESSAGE.E_MAIL_VALIDATION),
              },
            }}
          />
          <TextInput
            id="password"
            type="password"
            label={t('signin_screen_title_password')}
            error={errors?.password?.message}
            showDeleteIcon={watch()?.password}
            showVisibleIcon={true}
            onDelete={() => setValue('password', '')}
            register={register}
            placeholder={t('signin_screen_title_password')}
            rules={{
              pattern: REGEX.LOGIN_PASSWORD,
            }}
          />
          <Link href="/auth/findpassword">
            <a>
              <p className="password-info-text">
                {t('signin_screen_label_find_password')}
              </p>
            </a>
          </Link>
          <div className="button-container">
            <Button
              type="submit"
              title={t('common_screen_title_login')}
              isDisable={!isValid || !watch()?.email || !watch()?.password}
              customCSS={buttonStyle}
            />
          </div>
        </form>
      </main>
      {(loading || refreshTokenLoading || initUserLoading) && <SPLoading />}
    </>
  );
};

const buttonStyle = css`
  margin-top: 2.5em;
  width: 100%;
  height: 3rem;
  font-size: 1em;
  cursor: pointer;
  font: ${theme.font.body1Bold};
`;

const container = css`
  padding: 4em 1.25em 4em;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 800px;
  background: ${theme.colors.white};

  .login-form {
    width: 100%;
    margin-top: 0.5em;
  }

  .password-info-text {
    margin-top: 1.25em;
    text-decoration: underline;
    color: ${theme.colors.gray700};
    font-size: 14px;
    cursor: pointer;
    float: right;
  }

  .buttonContainer {
    margin: 40px 20px;
  }

  @media (max-width: ${theme.devices.mobile}) {
    width: 100%;
  }
`;

export default LoginPage;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/auth/login',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  